<template>
  <BLayoutList
    :title="pageTitle"
    :skeleton="isLoadingAsyncData"
    class="ViewReportsLKList"
  >
    <template #before-add>
      <BMenuMore
        :list="menuSelected"
        :disabled="isLoadingCreate"
        tooltip="Выбрать"
        class="mr-2"
        @selected="onSelectSelectedMenu"
      >
        <template #activator>
          <v-btn
            :color="color"
            dense
            depressed
            dark
            :loading="isLoadingCreate"
            :class="{ 'is-disabled': isLoadingCreate }"
            class="ViewReportsLKList__button"
            v-text="'Обновить'"
          />
        </template>
      </BMenuMore>
    </template>

    <BTableCommon
      v-model="selected"
      type="reports"
      :list="sortedList"
      :pagination="pagination"
      :ordering="filters.ordering"
      :loading="isLoading"
      :skeleton="isLoadingAsyncData"
      @change:pagination="onChangePagination"
      @change:sort="onChangeSort"
      @action:delete="onDeleteWithRefresh"
      @update:status="onUpdateStatus"
    />
  </BLayoutList>
</template>

<script>
/* eslint-disable import/no-unresolved, import/extensions */
/** @typedef { import('@/helpers/mixins/createListMixin').ListMixin<any, StoreModule> } List */

import variables from '@/assets/scss/exports.scss'
import { createListMixin } from '@/helpers/mixins/createListMixin'

import { LKReportModel as Model } from './Model'
import {
  LKReportStoreModule as StoreModule,
  LKReportMapper as StoreMapper,
} from './store'

import BLayoutList from '@/components/layouts/BLayoutList.vue'
import BTableCommon from '@/components/tables/BTableCommon.vue'
import BMenuMore from '@/components/common/BMenuMore.vue'
import { ReportCodes, ReportStatuses } from '@/typings/api/Report'
import { rootMapper } from '@/store/modules/root'
import { createYearModal } from './utils'

const mixinOptions = { Model, StoreModule }
const listMixin = createListMixin(mixinOptions)

const MENU_SELECTED = {
  BASE: {
    id: ReportCodes.basicStatistics,
    title: 'Базовый отчет ДК 500+',
    code: ReportCodes.basicStatistics,
  },

  STATISTICS: {
    id: ReportCodes.directionEventStatistics,
    title: 'Статистика по мерам и направлениям',
    code: ReportCodes.directionEventStatistics,
  },

  ROADMAP: {
    id: ReportCodes.common,
    title: 'Отчет в формате дорожных карт',
    code: ReportCodes.common,
  },

  ACTIVITY: {
    id: ReportCodes.userActivity,
    title: 'Отчёт о последней активности пользователей по ДК',
    code: ReportCodes.userActivity,
  },

  EXPERT: {
    id: ReportCodes.expert,
    title: 'Отчет по экспертизе ДК',
    code: ReportCodes.expert,
  },

  ALL: {
    id: ReportCodes.all,
    title: 'Все',
    code: ReportCodes.all,
  },
}

export default {
  name: 'ViewReportsLKList',
  components: {
    BLayoutList,
    BTableCommon,
    BMenuMore,
  },
  mixins: [listMixin],
  data: () => ({
    isLoadingCreate: false,
    color: variables['webflow-color-blue'],
  }),
  computed: {
    ...rootMapper.mapGetters(['profile']),

    menuSelected() {
      return Object.values(MENU_SELECTED).filter((el) => {
        if (el.id === ReportCodes.userActivity) {
          return this.profile.is_superuser
        }
        return true
      })
    },

    sortedList() {
      const order = {
        [ReportCodes.common]: 3,
        [ReportCodes.basicStatistics]: 1,
        [ReportCodes.directionEventStatistics]: 2,
        [ReportCodes.userActivity]: 4,
      }
      return this.menuSelected
        .map((el) => {
          if (el.code !== ReportCodes.all) {
            const data = this.list?.find((elData) => elData.code === el.code)
            return data || {
              title: el.title,
              code: el.code,
              status: ReportStatuses.ready,
            }
          }
          return null
        })
        .filter(Boolean)
        .sort((a, b) => order[a.code] - order[b.code])
    },
  },
  watch: {
    list(v) {
      if (v.length <= 0) {
        // Иначе значения по умолчанию не будут показываться, так как при пустом ответе от API кол-во = 0.
        this.pagination.count = 3
      }
    },
  },
  methods: {
    ...StoreMapper.mapActions(['getOneAndUpdateList', 'restart']),

    async onCreate(code, params = {}) {
      this.isLoadingCreate = true
      const payload = {
        code,
        params: {
          ...params,
        },
      }

      try {
        await this.create({ payload })
        this.onGetData(this.filters)
      } catch (e) {
        this.$notice.errors(e)
      }

      this.isLoadingCreate = false
    },
    async onSelectSelectedMenu(data) {
      if (data.code === ReportCodes.all) {
        this.menuSelected
          .forEach((element) => {
            if (element.code !== ReportCodes.all) {
              this.onCreate(element.code)
            }
          })
      } else if (data.code === ReportCodes.expert) {
        const modal = await createYearModal(this, {
          on: {
            ready: (year) => this.onCreate(data.code, { year }),
          },
        })
        await modal.open()
      } else {
        this.onCreate(data.code)
      }
    },
    async onUpdateStatus(data, callback) {
      let action
      switch (data.status) {
        case ReportStatuses.ready:
          this.onCreate(data.code)
          break

        case ReportStatuses.pending:
          action = this.getOneAndUpdateList(data)
          break

        case ReportStatuses.error:
          action = this.restart(data)
          break

        default:
      }

      if (!action) return

      const unwatch = callback?.()

      try {
        await action
      } catch (e) {
        this.$notice.errors(e)
      }

      unwatch?.()
    },
  },
}
</script>

<style lang="scss">
.ViewReportsLKList {
  &__button {
    &.is-disabled {
      pointer-events: none;
      cursor: default;
      opacity: 0.5;
    }
  }

  .container {
    padding: 0;
    padding-bottom: 20px;
  }
}
</style>
